import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const plans = [
  {
    name: "Trial",
    credits: 5,
    usd: "$1",
    ngn: "₦900",
  },
  { name: "Micro", credits: 9, usd: "$3", ngn: "₦3,000" },
  { name: "Starter", credits: 20, usd: "$6", ngn: "₦6,000" },
  { name: "Basic", credits: 45, usd: "$13", ngn: "₦13,000" },
  { name: "Pro", credits: 100, usd: "$25", ngn: "₦25,000" },
  { name: "Enterprise", credits: 250, usd: "$55", ngn: "₦55,000" },
];

export default function PricingPage() {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-3" data-testid="pricing-brand-link">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-slate-200">
              <img src="/logo.png" alt="DocMania logo" className="h-8 w-8 object-contain" />
            </div>
            <span className="text-xl font-extrabold tracking-tight text-slate-900">
              <span className="text-blue-600">Doc</span>
              <span className="text-orange-500">Mania</span>
            </span>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-12">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-8">
          <ArrowLeft className="h-4 w-4" /> Back to Home
        </Link>
        <h1 className="text-3xl font-bold text-foreground mb-4" data-testid="pricing-title">Pricing</h1>

        <div className="prose prose-sm text-foreground/90 space-y-6">
          <p>DocMania uses a simple credit system — no subscriptions, no recurring charges. Buy credits once, use them whenever you need a document.</p>

          <p>Prices are charged in USD for international customers and NGN for Nigerian customers, based on your account region.</p>

          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full border-collapse text-left" data-testid="pricing-table">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 font-semibold text-slate-900">Plan</th>
                  <th className="px-4 py-3 font-semibold text-slate-900">Credits</th>
                  <th className="px-4 py-3 font-semibold text-slate-900">USD</th>
                  <th className="px-4 py-3 font-semibold text-slate-900">NGN</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {plans.map((plan) => (
                  <tr key={plan.name}>
                    <td className="px-4 py-3 font-medium text-slate-900">{plan.name}</td>
                    <td className="px-4 py-3">{plan.credits}</td>
                    <td className="px-4 py-3">{plan.usd}</td>
                    <td className="px-4 py-3">{plan.ngn}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-sm text-muted-foreground !mt-3">
            The Trial Pack is for new customers only: one purchase per account, and only if you have never completed a credit purchase. After that, choose any of the standard packs above. All plans are one-time credit purchases — no subscriptions, no auto-renewal. Credits are used to generate documents and never expire.
          </p>

          <section>
            <h2 className="text-lg font-semibold">How credits work</h2>
            <p>Each document you generate uses a set number of credits depending on the document type and complexity. Credits never expire, so you can top up as often — or as rarely — as you need.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold">What&apos;s included</h2>
            <p>Every plan gives you access to our full template library — invoices, quotations, proposals, resumes, and more — plus optional smart content suggestions to help you write faster.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold">FAQ</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold">Do credits expire?</h3>
                <p>No, credits remain in your account indefinitely.</p>
              </div>
              <div>
                <h3 className="font-semibold">Can I buy more credits later?</h3>
                <p>Yes, you can top up at any time from your dashboard.</p>
              </div>
              <div>
                <h3 className="font-semibold">What payment methods do you accept?</h3>
                <p>Available payment methods are shown at checkout.</p>
              </div>
              <div>
                <h3 className="font-semibold">Is there a trial pack?</h3>
                <p>
                  Yes — a one-time Trial Pack (5 credits) for accounts that have never completed a credit purchase.
                  It is not a free trial and is not available after your first successful purchase.
                </p>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
